export type AbVariant = {
  key: string;
  weight: number;
};

export type AbExperiment = {
  id: string;
  cookieName: string;
  variants: readonly AbVariant[];
  cookieMaxAgeSeconds: number;
};

export type ExperimentEventParams = {
  experiment_id?: string;
  variant?: string;
};

function getCookieValue(name: string) {
  if (typeof document === "undefined") return undefined;

  const cookie = document.cookie.split("; ").find((row) => row.startsWith(`${encodeURIComponent(name)}=`));

  return cookie ? decodeURIComponent(cookie.split("=")[1] ?? "") : undefined;
}

function setCookieValue(name: string, value: string, maxAgeSeconds: number) {
  if (typeof document === "undefined") return;

  document.cookie = [
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
    `Max-Age=${maxAgeSeconds}`,
    "Path=/",
    "SameSite=Lax",
  ].join("; ");
}

function isValidVariant(experiment: AbExperiment, value: string | undefined) {
  return experiment.variants.some((variant) => variant.key === value);
}

function pickWeightedVariant(experiment: AbExperiment) {
  const totalWeight = experiment.variants.reduce((sum, variant) => sum + variant.weight, 0);
  const randomValue = Math.random() * totalWeight;

  let cursor = 0;
  for (const variant of experiment.variants) {
    cursor += variant.weight;
    if (randomValue < cursor) return variant.key;
  }

  return experiment.variants[0]?.key ?? "control";
}

export function getAssignedExperimentVariant(experiment: AbExperiment) {
  const savedVariant = getCookieValue(experiment.cookieName);
  return isValidVariant(experiment, savedVariant) ? savedVariant : undefined;
}

export function getOrAssignExperimentVariant(experiment: AbExperiment) {
  const savedVariant = getAssignedExperimentVariant(experiment);
  if (savedVariant) return savedVariant;

  const variant = pickWeightedVariant(experiment);
  setCookieValue(experiment.cookieName, variant, experiment.cookieMaxAgeSeconds);
  return variant;
}

export function getExperimentEventParams(experiment: AbExperiment): ExperimentEventParams {
  const variant = getAssignedExperimentVariant(experiment);

  if (!variant) return {};

  return {
    experiment_id: experiment.id,
    variant,
  };
}
